import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyEsagaiaMode({ brand, today, Copy }: ModeRendererProps) {
  // ESA GAIA gradient: stellar cartography blue to cosmic gold
  const gaiaGradient = (
    <defs>
      <linearGradient id="gaiaGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0B3D91" />
        <stop offset="50%" stopColor="#4169E1" />
        <stop offset="100%" stopColor="#FFD700" />
      </linearGradient>
      <radialGradient id="starGlow" cx="50%" cy="50%">
        <stop offset="0%" stopColor="#FFD700" stopOpacity={0.8} />
        <stop offset="100%" stopColor="#0B3D91" stopOpacity={0} />
      </radialGradient>
    </defs>
  );

  // Parallax grids: distance measurement
  const parallaxGrids: JSX.Element[] = [];
  for (let i = 0; i < 20; i++) {
    const radius = 60 + i * 18;
    const segments = 24;
  const points: string[] = [];
    for (let j = 0; j < segments + 1; j++) {
      const angle = (j * Math.PI * 2) / segments;
      const offset = Math.sin(j * 3) * 5;
      points.push(`${397 + (radius + offset) * Math.cos(angle)},${561.5 + (radius + offset) * Math.sin(angle)}`);
    }
    parallaxGrids.push(
      <polyline
        key={`parallax-${i}`}
        points={points.join(" ")}
        fill="none"
        stroke="url(#gaiaGrad)"
        strokeWidth={0.5}
        opacity={0.3}
      />
    );
  }

  // Proper-motion trails: stellar movement vectors
  const properMotionTrails: JSX.Element[] = [];
  for (let i = 0; i < 36; i++) {
    const angle = (i * Math.PI * 2) / 36;
    const radius = 180;
    const x1 = 397 + radius * Math.cos(angle);
    const y1 = 561.5 + radius * Math.sin(angle);
    const motionAngle = angle + Math.PI / 6;
    const trailLength = 40;
    const x2 = x1 + trailLength * Math.cos(motionAngle);
    const y2 = y1 + trailLength * Math.sin(motionAngle);
    properMotionTrails.push(
      <g key={`motion-${i}`}>
        <line
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="url(#gaiaGrad)"
          strokeWidth={1}
          opacity={0.4}
        />
        <circle cx={x1} cy={y1} r={3} fill="#FFD700" opacity={0.6} />
      </g>
    );
  }

  // Density tiles: star concentration map
  const densityTiles: JSX.Element[] = [];
  for (let row = 0; row < 16; row++) {
    for (let col = 0; col < 12; col++) {
      const x = 80 + col * 55;
      const y = 120 + row * 55;
      const dx = x - 397;
      const dy = y - 561.5;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const density = Math.max(0, 1 - dist / 400);
      const size = 8 + density * 16;
      densityTiles.push(
        <rect
          key={`tile-${row}-${col}`}
          x={x - size / 2}
          y={y - size / 2}
          width={size}
          height={size}
          fill="url(#gaiaGrad)"
          opacity={density * 0.5}
          rx={2}
        />
      );
    }
  }

  // Astrometric constellation: measured star positions
  const astrometricStars: JSX.Element[] = [];
  for (let i = 0; i < 120; i++) {
    const angle = Math.random() * Math.PI * 2;
    const radius = 50 + Math.random() * 300;
    const x = 397 + radius * Math.cos(angle);
    const y = 561.5 + radius * Math.sin(angle);
    const magnitude = 1 + Math.random() * 2.5;
    astrometricStars.push(
      <circle
        key={`star-${i}`}
        cx={x}
        cy={y}
        r={magnitude}
        fill="#FFD700"
        opacity={0.4 + Math.random() * 0.4}
      />
    );
  }

  // Central GAIA satellite
  const gaiaSatellite = (
    <g opacity={0.9}>
      <circle cx={397} cy={561.5} r={90} fill="url(#starGlow)" />
      <circle cx={397} cy={561.5} r={65} fill="none" stroke="#FFD700" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={45} fill="none" stroke="#4169E1" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={25} fill="none" stroke="#0B3D91" strokeWidth={2} />
      {/* Scan beams */}
      <line x1={397} y1={471.5} x2={397} y2={651.5} stroke="#FFD700" strokeWidth={2} opacity={0.6} />
      <line x1={307} y1={561.5} x2={487} y2={561.5} stroke="#FFD700" strokeWidth={2} opacity={0.6} />
    </g>
  );

  return (
    <g>
      {gaiaGradient}
      {astrometricStars}
      {densityTiles}
      {parallaxGrids}
      {properMotionTrails}
      {gaiaSatellite}
      <g className="mode-copy" transform="translate(48, 840)" fill="#FFFFFF" fontFamily="monospace">
        <text x={0} y={0} fontSize={18} fontWeight="bold">{Copy?.title}</text>
        <text x={0} y={22} fontSize={14}>{Copy?.subtitle}</text>
        <text x={0} y={44} fontSize={12} opacity={0.85}>{Copy?.date}</text>
      </g>
    </g>
  );
}
